import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Leaf, SpinnerGap } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking"); // checking, success, error
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const polledRef = useRef(false);

  useEffect(() => {
    if (!sessionId || polledRef.current) return;
    polledRef.current = true;

    let attempts = 0;
    const maxAttempts = 8;
    const pollInterval = 2000;

    const poll = async () => {
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/checkout/status/${sessionId}`,
          { withCredentials: false }
        );
        if (data.payment_status === "paid") {
          setStatus("success");
          await refreshUser();
          return;
        }
        if (data.status === "expired") {
          setStatus("error");
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setStatus("error");
        }
      } catch {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setStatus("error");
        }
      }
    };

    poll();
  }, [sessionId, refreshUser]);

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-leather flex items-center justify-center p-4" data-testid="payment-checking">
        <div className="text-center">
          <SpinnerGap size={48} className="animate-spin text-white mx-auto mb-4" />
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-white mb-2">Processing payment...</h2>
          <p className="text-white/70 font-['Manrope']">Please wait while we confirm your purchase.</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-leather flex items-center justify-center p-4" data-testid="payment-error">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl border border-[#D2C2B3] p-8 text-center max-w-md shadow-lg">
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#2C3E2D] mb-2">Payment Issue</h2>
          <p className="text-[#7C8068] font-['Manrope'] mb-6">
            We couldn't confirm your payment. If you were charged, please contact support.
          </p>
          <Button
            onClick={() => navigate("/pricing")}
            data-testid="retry-payment-btn"
            className="bg-[#C25A3C] hover:bg-[#a84830] text-white rounded-xl font-['Manrope']"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-leather flex items-center justify-center p-4" data-testid="payment-success">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl border border-[#D2C2B3] p-8 text-center max-w-md shadow-lg">
        <div className="w-16 h-16 rounded-full bg-[#55624F] flex items-center justify-center mx-auto mb-4">
          <Check size={32} weight="bold" className="text-white" />
        </div>
        <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#2C3E2D] mb-2">Welcome to Nature Journal!</h2>
        <p className="text-[#7C8068] font-['Manrope'] mb-6">
          Your lifetime access is now active. Start documenting your nature adventures!
        </p>
        <Button
          onClick={() => navigate("/")}
          data-testid="start-journaling-btn"
          className="bg-[#55624F] hover:bg-[#3d4738] text-white rounded-xl font-['Manrope'] px-8"
        >
          <Leaf size={18} weight="duotone" className="mr-2" />
          Start Journaling
        </Button>
      </div>
    </div>
  );
}
